import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { Property } from "@shared/schema";
import { looksTerminalPropertyStatus } from "@shared/schema";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Trash2, ArrowRight } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { useBetaAccess } from "@/hooks/use-beta-access";
import { DARK_MAP_STYLES } from "@/lib/map-styles";
import { resolveBrandColor } from "@/lib/brand-colors";
import { isHiddenSectionEnabled } from "@shared/hidden-sections";
import {
  normalizePolygonOrder,
  type TrashRoute,
} from "@/components/trash-coverage-map";

// Client mirror of the server trash access gate (server/lib/trashAccess.ts):
// access requires the specific user's active trash-service beta grant.
const TRASH_BETA_KEY = "trash-service";

interface PropertyMapProps {
  properties: Property[];
  showLegend?: boolean;
  linkPattern?: string;
  /** Opt-in: show a toggle that overlays the trash service coverage zones. */
  showTrashCoverage?: boolean;
}

interface StatusConfig {
  label: string;
  markerUrl: string;
  color: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  active: {
    label: "Active",
    markerUrl: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
    color: "#16a34a",
  },
  underwriting: {
    label: "Underwriting",
    markerUrl: "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png",
    color: "#d97706",
  },
  renovations: {
    label: "Renovations",
    markerUrl: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png",
    color: "#ea580c",
  },
  launch: {
    label: "Launch",
    markerUrl: "https://maps.google.com/mapfiles/ms/icons/purple-dot.png",
    color: "#4f46e5",
  },
  acquisition: {
    label: "Acquisition",
    markerUrl: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
    color: "#2563eb",
  },
};

const FALLBACK_CONFIG: StatusConfig = {
  label: "Other",
  markerUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
  color: "#dc2626",
};

function getStatusConfig(status: string): StatusConfig {
  return STATUS_CONFIG[status] ?? FALLBACK_CONFIG;
}

export default function PropertyMap({ properties, showLegend, linkPattern = "/app/properties/:id", showTrashCoverage = false }: PropertyMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const trashPolygonsRef = useRef<google.maps.Polygon[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(new Set());
  const [trashCoverageOn, setTrashCoverageOn] = useState(false);

  const { theme } = useTheme();
  const isDark = theme === "dark";

  const { data: mapsKeyData } = useQuery<{ key: string }>({
    queryKey: ["/api/app/maps-key"],
  });

  // Fail-closed trash access gate. Without it a tier-locked host would see the
  // "Trash coverage" toggle on the (broadly-accessible) Properties page and
  // toggling it would fire a guaranteed-403 GET /api/app/trash/routes — a
  // silent doomed request the route-scoped upgrade modal never surfaces
  // (its key `operations.trash` doesn't match the Properties section).
  // B19 — the trash service is switched off in shared/hidden-sections.ts.
  const trashBetaGranted = useBetaAccess(TRASH_BETA_KEY) && isHiddenSectionEnabled("trash_service");
  const trashAllowed = showTrashCoverage && trashBetaGranted;

  const trashRoutesQuery = useQuery<TrashRoute[]>({
    queryKey: ["/api/app/trash/routes"],
    enabled: trashAllowed && trashCoverageOn,
    staleTime: 5 * 60_000,
  });
  const trashRoutes = trashRoutesQuery.data ?? [];

  const apiKey = mapsKeyData?.key || "";

  const loadGoogleMaps = useCallback(() => {
    if (!apiKey) return;
    if (window.google?.maps?.Map) {
      setMapLoaded(true);
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          setMapLoaded(true);
        }
      }, 100);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          setMapLoaded(true);
        }
      }, 50);
    };
    script.onerror = () => setMapError("Failed to load Google Maps");
    document.head.appendChild(script);
  }, [apiKey]);

  useEffect(() => {
    loadGoogleMaps();
  }, [loadGoogleMaps]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    const mappableProperties = properties.filter(
      (p) =>
        !looksTerminalPropertyStatus(p.status) &&
        p.latitude != null &&
        p.longitude != null &&
        !hiddenStatuses.has(p.status ?? "")
    );

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        zoom: 10,
        center: { lat: 33.749, lng: -84.388 },
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        styles: isDark ? DARK_MAP_STYLES : [],
      });
      infoWindowRef.current = new google.maps.InfoWindow();
    }

    const map = mapInstanceRef.current;
    const infoWindow = infoWindowRef.current!;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (mappableProperties.length === 0) return;

    const bounds = new google.maps.LatLngBounds();

    mappableProperties.forEach((prop) => {
      const lat = prop.latitude!;
      const lng = prop.longitude!;
      const position = { lat, lng };
      bounds.extend(position);

      const cfg = getStatusConfig(prop.status ?? "");

      const marker = new google.maps.Marker({
        map,
        position,
        title: prop.name,
        icon: cfg.markerUrl,
      });

      marker.addListener("click", () => {
        const dark = document.documentElement.classList.contains("dark");
        const bg = dark ? "#1e1e1e" : "#ffffff";
        const textPrimary = dark ? "#f3f4f6" : "#111827";
        const textSecondary = dark ? "#9ca3af" : "#6b7280";
        const textStatus = dark ? "#d1d5db" : "#374151";
        const linkColor = resolveBrandColor();

        const container = document.createElement("div");
        container.style.cssText = `padding:8px;max-width:250px;font-family:system-ui,sans-serif;background:${bg};border-radius:4px;`;

        const nameEl = document.createElement("div");
        nameEl.style.cssText = `font-weight:600;font-size:14px;margin-bottom:4px;color:${textPrimary};`;
        nameEl.textContent = prop.name;
        container.appendChild(nameEl);

        if (prop.address) {
          const addrEl = document.createElement("div");
          addrEl.style.cssText = `font-size:12px;color:${textSecondary};margin-bottom:6px;`;
          addrEl.textContent = prop.address;
          container.appendChild(addrEl);
        }

        const statusRow = document.createElement("div");
        statusRow.style.cssText = "display:flex;align-items:center;gap:6px;margin-bottom:8px;";
        const dot = document.createElement("span");
        dot.style.cssText = `display:inline-block;width:8px;height:8px;border-radius:50%;background:${cfg.color};`;
        const statusText = document.createElement("span");
        statusText.style.cssText = `font-size:12px;color:${textStatus};`;
        statusText.textContent = cfg.label;
        statusRow.appendChild(dot);
        statusRow.appendChild(statusText);
        container.appendChild(statusRow);

        const link = document.createElement("a");
        link.href = linkPattern.replace(":id", String(prop.id));
        link.style.cssText = `font-size:12px;color:${linkColor};text-decoration:none;font-weight:500;`;
        link.textContent = "View Details →";
        link.setAttribute("data-testid", `link-map-property-${prop.id}`);
        container.appendChild(link);

        infoWindow.setContent(container);
        infoWindow.open(map, marker);
      });

      markersRef.current.push(marker);
    });

    if (mappableProperties.length === 1) {
      const lat = mappableProperties[0].latitude!;
      const lng = mappableProperties[0].longitude!;
      map.setCenter({ lat, lng });
      map.setZoom(14);
    } else {
      map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
    }
  }, [mapLoaded, properties, linkPattern, hiddenStatuses, isDark]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setOptions({ styles: isDark ? DARK_MAP_STYLES : [] });
  }, [isDark]);

  // Draw / clear the trash service coverage zones when toggled.
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current) return;
    const map = mapInstanceRef.current;

    trashPolygonsRef.current.forEach((p) => p.setMap(null));
    trashPolygonsRef.current = [];

    if (!trashCoverageOn || trashRoutes.length === 0) return;

    const brandColor = resolveBrandColor();
    trashRoutes.forEach((route) => {
      if (!Array.isArray(route.polygon) || route.polygon.length < 3) return;
      const path = normalizePolygonOrder(route.polygon).map(([lat, lng]) => ({ lat, lng }));
      const polygon = new google.maps.Polygon({
        paths: path,
        strokeColor: brandColor,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: brandColor,
        fillOpacity: 0.12,
        clickable: false,
        map,
      });
      trashPolygonsRef.current.push(polygon);
    });
  }, [mapLoaded, trashCoverageOn, trashRoutes]);

  if (!apiKey) {
    return null;
  }

  if (mapError) {
    return (
      <div className="border rounded-lg p-6 text-center text-gray-500 mb-6" data-testid="map-error">
        <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">{mapError}</p>
      </div>
    );
  }

  const mappableProperties = properties.filter(
    (p) => !looksTerminalPropertyStatus(p.status) && p.latitude != null && p.longitude != null
  );

  if (mappableProperties.length === 0 && properties.length > 0) {
    return null;
  }

  if (!mapLoaded) {
    return <Skeleton className="h-[350px] w-full rounded-lg mb-6" data-testid="map-loading" />;
  }

  const presentStatuses = Array.from(
    new Set(
      properties
        .filter((p) => !looksTerminalPropertyStatus(p.status) && p.latitude != null && p.longitude != null)
        .map((p) => p.status ?? "")
    )
  ).filter((s) => s !== "");

  const orderedStatuses = Object.keys(STATUS_CONFIG).filter((s) =>
    presentStatuses.includes(s)
  );
  const unknownStatuses = presentStatuses.filter((s) => !STATUS_CONFIG[s]);
  const legendStatuses = [...orderedStatuses, ...unknownStatuses];

  return (
    <div className="mb-6" data-testid="property-map-container">
      <div
        ref={mapRef}
        className="w-full h-[350px] rounded-lg border shadow-sm"
        data-testid="property-map"
      />
      {showLegend && legendStatuses.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-2 px-1" data-testid="map-legend">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mr-2">Filter:</span>
          {legendStatuses.map((status) => {
            const cfg = getStatusConfig(status);
            const isHidden = hiddenStatuses.has(status);
            return (
              <button
                key={status}
                type="button"
                onClick={() => {
                  setHiddenStatuses((prev) => {
                    const next = new Set(prev);
                    if (next.has(status)) {
                      next.delete(status);
                    } else {
                      next.add(status);
                    }
                    return next;
                  });
                }}
                aria-pressed={!isHidden}
                title={isHidden ? `Show ${cfg.label}` : `Hide ${cfg.label}`}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs transition-colors hover-elevate active-elevate-2 ${
                  isHidden
                    ? "border-border bg-muted text-muted-foreground line-through opacity-60"
                    : "border-border bg-card text-foreground"
                }`}
                data-testid={`legend-toggle-${status}`}
              >
                <span
                  className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: isHidden ? "#9ca3af" : cfg.color }}
                />
                <span>{cfg.label}</span>
              </button>
            );
          })}
        </div>
      )}
      {trashAllowed && (
        <div className="flex flex-wrap items-center gap-2 mt-2 px-1" data-testid="trash-coverage-controls">
          <button
            type="button"
            onClick={() => setTrashCoverageOn((v) => !v)}
            aria-pressed={trashCoverageOn}
            title={trashCoverageOn ? "Hide trash service coverage" : "Show trash service coverage"}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs transition-colors hover-elevate active-elevate-2 ${
              trashCoverageOn
                ? "border-brand bg-brand/10 text-foreground"
                : "border-border bg-card text-foreground"
            }`}
            data-testid="toggle-trash-coverage"
          >
            <Trash2 className="h-3.5 w-3.5 flex-shrink-0" />
            <span>Trash coverage</span>
          </button>
          {trashCoverageOn && trashRoutesQuery.isLoading && (
            <span className="text-xs text-muted-foreground" data-testid="text-trash-coverage-loading">
              Loading coverage…
            </span>
          )}
          {trashCoverageOn && !trashRoutesQuery.isLoading && (
            <Link
              href="/app/operations/trash"
              className="flex items-center gap-1 px-2 py-1 rounded-md border border-brand bg-brand text-brand-foreground text-xs font-medium transition-colors hover-elevate active-elevate-2"
              data-testid="link-enroll-trash-pickup"
            >
              Enroll for trash pickup?
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
